/**
 * Entities API service for managing client entities.
 * @module lib/api/entities.api
 */

import { emptyResponseToNull } from './api-client';
import { BaseApi } from './base-api';
import { createSingletonApi } from './singleton-factory';
import { trackActivationMilestone } from '@/lib/analytics';
import { buildUpdatePayload } from '@/utils/saveCustomField';
import {
  Entity,
  CreateEntity,
  UpdateEntity,
  PaginatedResponse,
  ImportPreviewRecord,
  ImportResult,
  ImportOptions,
  ExportOptions
} from '@scrambl/shared';
import { EntityFilters } from '@/store/entity-filters.store';

interface UpdateEntityFieldParams {
  entityId: string;
  field: string;
  value: unknown;
}

export type EntityTimelineItemType =
  | 'communication'
  | 'ticket'
  | 'event'
  | 'thread'
  | 'meeting_recording';

export interface EntityTimelineItem {
  id: string;
  type: EntityTimelineItemType;
  title: string;
  summary: string | null;
  occurredAt: string;
  source: Record<string, unknown>;
}

export interface EntityTimelineResponse {
  entityId: string;
  items: EntityTimelineItem[];
  counts: Record<EntityTimelineItemType, number>;
}

export type EntityHealthStatus = 'healthy' | 'watch' | 'risk';
export type EntityHealthScoringMode = 'baseline' | 'cold_start';

export interface EntityHealthAxisBaseline {
  mode: EntityHealthScoringMode;
  sampleSize: number;
  minimumSampleSize: number;
  reason: string;
  metrics?: Record<string, number | null>;
}

export interface EntityHealthTrend {
  direction: 'up' | 'down' | 'flat';
  delta: number;
  since: string;
}

export interface EntityHealthFactor {
  key: string;
  label: string;
  detail: string;
  direction: 'helping' | 'harming' | 'neutral';
  contribution: number;
  trend?: EntityHealthTrend | null;
}

export interface EntityHealthAxis {
  score: number;
  status: EntityHealthStatus;
  scoringMode: EntityHealthScoringMode;
  absoluteScore: number;
  baseline: EntityHealthAxisBaseline;
  signals: Record<string, unknown>;
  factors: EntityHealthFactor[];
  trend?: EntityHealthTrend | null;
}

export interface EntityHealthHumanOverride {
  id: string | null;
  active: boolean;
  score: number | null;
  reason: string | null;
  computedOverallScore: number;
  divergence: number | null;
  updatedAt: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
}

export interface EntityHealthVectorResponse {
  entityId: string;
  entityName: string;
  overallScore: number;
  computedOverallScore: number;
  axes: {
    relationship: EntityHealthAxis;
    delivery: EntityHealthAxis;
    commercial: EntityHealthAxis;
  };
  planVariance: {
    hasPlan: boolean;
    targetOverallScore: number | null;
    overallDelta: number | null;
    axes: {
      relationship: { target: number | null; delta: number | null };
      delivery: { target: number | null; delta: number | null };
      commercial: { target: number | null; delta: number | null };
    };
    commercial: {
      targetAnnualRevenue: number | null;
      revenueDelta: number | null;
      targetMarginPercent: number | null;
      marginPercentDelta: number | null;
    };
  };
  humanOverride: EntityHealthHumanOverride;
  scoringProfile: {
    source: 'organization' | 'default';
    axisWeights: {
      relationship: number;
      delivery: number;
      commercial: number;
    };
    thresholds: {
      healthy: number;
      watch: number;
    };
    minimumSamples: {
      relationship: number;
      delivery: number;
      commercial: number;
    };
  };
  computedAt: string;
}

export interface EntityHealthTriageItem {
  entityId: string;
  entityName: string;
  lifecycleStage: string;
  overallScore: number;
  computedOverallScore: number;
  relationshipScore: number;
  deliveryScore: number;
  commercialScore: number;
  annualRevenue: number;
  marginPercent: number;
  relationshipRisk: number;
  valueAtRisk: number;
  triageScore: number;
  priority: 'critical' | 'high' | 'medium' | 'low';
  alertRecommended: boolean;
  alertType: 'client_health_risk';
  reasons: string[];
  humanOverrideActive: boolean;
  planOverallDelta: number | null;
  computedAt: string;
}

export interface EntityHealthTriageResponse {
  items: EntityHealthTriageItem[];
  summary: {
    evaluated: number;
    alertRecommended: number;
    critical: number;
    high: number;
    totalValueAtRisk: number;
  };
  computedAt: string;
}

export interface EntityHealthAlertSyncResponse {
  ruleId: string;
  created: number;
  alreadyActive: number;
  resolved: number;
  candidates: number;
}

export interface EntityAccountPlan {
  id: string;
  organizationId: string;
  entityId: string;
  strategy: string | null;
  objectives: unknown[];
  risks: unknown[];
  successMetrics: unknown[];
  multiYearTargets: unknown[];
  targetAnnualRevenue: number | null;
  targetMarginPercent: number | null;
  targetRelationshipScore: number | null;
  targetDeliveryScore: number | null;
  targetCommercialScore: number | null;
  targetOverallScore: number | null;
  renewalDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertEntityAccountPlan {
  strategy?: string | null;
  objectives?: unknown[];
  risks?: unknown[];
  successMetrics?: unknown[];
  multiYearTargets?: unknown[];
  targetAnnualRevenue?: number | null;
  targetMarginPercent?: number | null;
  targetRelationshipScore?: number | null;
  targetDeliveryScore?: number | null;
  targetCommercialScore?: number | null;
  targetOverallScore?: number | null;
  renewalDate?: string | null;
  notes?: string | null;
}

export interface EntityHealthOverrideRecord {
  id: string;
  organizationId: string;
  entityId: string;
  score: number;
  reason: string;
  active: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  clearedBy: string | null;
  clearedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertEntityHealthOverride {
  score: number;
  reason: string;
}

/**
 * Transform entity data to flatten custom fields from nested object to top-level with custom_ prefix
 */
export const flattenEntityCustomFields = (entity: Entity): Entity => {
  const result = { ...entity };

  // Flatten custom fields from nested customFields object to top-level with custom_ prefix
  if (result.customFields && typeof result.customFields === 'object') {
    Object.entries(result.customFields).forEach(([fieldName, value]) => {
      const flattenedKey = `custom_${fieldName}`;
      (result as Record<string, unknown>)[flattenedKey] = value;
    });
  }

  return result;
};

class EntityApiService extends BaseApi<Entity, CreateEntity, UpdateEntity> {
  constructor() {
    super({
      baseEndpoint: '/entities',
      responseNormalization: true
    });
  }

  // Get all entities with optional filters
  async getAll(params?: EntityFilters): Promise<PaginatedResponse<Entity>> {
    const queryParams = this.buildQueryParams((params || {}) as Record<string, unknown>);
    const response = await this.makeRequest<PaginatedResponse<Entity>>(`${this.config.baseEndpoint}${queryParams}`);
    // Flatten custom fields for each entity
    return {
      ...response,
      data: response.data.map(flattenEntityCustomFields)
    };
  }

  // Get entity by ID
  async getById(id: string): Promise<Entity> {
    const entity = await this.makeRequest<Entity>(`${this.config.baseEndpoint}/${id}`);
    return flattenEntityCustomFields(entity);
  }

  // Create entity
  async create(data: CreateEntity): Promise<Entity> {
    const created = await this.makeRequest<Entity>(this.config.baseEndpoint, {
      method: 'POST',
      body: data
    });

    // Entities are the agency's clients. Instrumented here because the
    // contacts dialogs create companies inline, bypassing the mutation hook.
    trackActivationMilestone('client_added', {
      has_entity_type: !!created.entityTypeId
    });

    return created;
  }

  // Update entity
  async update(id: string, data: Partial<UpdateEntity>): Promise<Entity> {
    const entity = await this.makeRequest<Entity>(`${this.config.baseEndpoint}/${id}`, {
      method: 'PATCH',
      body: data
    });
    return flattenEntityCustomFields(entity);
  }

  // Delete entity
  async delete(id: string): Promise<void> {
    await this.makeRequest<void>(`${this.config.baseEndpoint}/${id}`, { method: 'DELETE' });
  }

  // Archive entity (recordState → 'archived'; cascades to its projects)
  async archive(id: string): Promise<Entity> {
    const entity = await this.makeRequest<Entity>(`${this.config.baseEndpoint}/${id}/archive`, {
      method: 'POST'
    });
    return flattenEntityCustomFields(entity);
  }

  // Unarchive entity (restores the projects archived by the cascade too)
  async unarchive(id: string): Promise<Entity> {
    const entity = await this.makeRequest<Entity>(`${this.config.baseEndpoint}/${id}/unarchive`, {
      method: 'POST'
    });
    return flattenEntityCustomFields(entity);
  }

  getModuleCounts(): Promise<{ allEntities: number }> {
    return this.makeRequest<{ allEntities: number }>(`${this.config.baseEndpoint}/module-counts`);
  }

  // Cascade: bulk-promote lead/qualified Contacts at this Entity to the
  // org's Customer-typed lifecycle stage. Called from the UI after an Entity
  // promotion to a customer stage when the user accepts the prompt.
  promoteContactsToCustomer(entityId: string): Promise<{ promotedCount: number; targetStageId: string | null }> {
    return this.makeRequest<{ promotedCount: number; targetStageId: string | null }>(
      `${this.config.baseEndpoint}/${entityId}/promote-contacts-to-customer`,
      { method: 'POST' },
    );
  }

  getTimeline(entityId: string, limit = 50): Promise<EntityTimelineResponse> {
    const searchParams = new URLSearchParams();
    searchParams.set('limit', String(limit));
    return this.makeRequest<EntityTimelineResponse>(
      `${this.config.baseEndpoint}/${entityId}/timeline?${searchParams.toString()}`,
    );
  }

  getHealthVector(entityId: string): Promise<EntityHealthVectorResponse> {
    return this.makeRequest<EntityHealthVectorResponse>(
      `${this.config.baseEndpoint}/${entityId}/health-vector`,
    );
  }

  getHealthTriage(params?: {
    limit?: number;
    evaluationLimit?: number;
  }): Promise<EntityHealthTriageResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit != null) searchParams.set('limit', String(params.limit));
    if (params?.evaluationLimit != null) {
      searchParams.set('evaluationLimit', String(params.evaluationLimit));
    }
    const query = searchParams.toString();
    return this.makeRequest<EntityHealthTriageResponse>(
      `${this.config.baseEndpoint}/health-triage${query ? `?${query}` : ''}`,
    );
  }

  syncHealthTriageAlerts(): Promise<EntityHealthAlertSyncResponse> {
    return this.makeRequest<EntityHealthAlertSyncResponse>(
      `${this.config.baseEndpoint}/health-triage/alerts/sync`,
      { method: 'POST' },
    );
  }

  recalculateHealthScores(limit?: number): Promise<{ updated: number }> {
    return this.makeRequest<{ updated: number }>(
      `${this.config.baseEndpoint}/health-scores/recalculate`,
      {
        method: 'POST',
        body: limit ? { limit } : {},
      },
    );
  }

  async getAccountPlan(entityId: string): Promise<EntityAccountPlan | null> {
    return emptyResponseToNull(
      await this.makeRequest<EntityAccountPlan | null>(
        `${this.config.baseEndpoint}/${entityId}/account-plan`,
      ),
    );
  }

  upsertAccountPlan(
    entityId: string,
    data: UpsertEntityAccountPlan,
  ): Promise<EntityAccountPlan> {
    return this.makeRequest<EntityAccountPlan>(
      `${this.config.baseEndpoint}/${entityId}/account-plan`,
      {
        method: 'PUT',
        body: data,
      },
    );
  }

  upsertHealthOverride(
    entityId: string,
    data: UpsertEntityHealthOverride,
  ): Promise<EntityHealthOverrideRecord> {
    return this.makeRequest<EntityHealthOverrideRecord>(
      `${this.config.baseEndpoint}/${entityId}/health-override`,
      {
        method: 'PUT',
        body: data,
      },
    );
  }

  clearHealthOverride(entityId: string): Promise<EntityHealthOverrideRecord | null> {
    return this.makeRequest<EntityHealthOverrideRecord | null>(
      `${this.config.baseEndpoint}/${entityId}/health-override`,
      { method: 'DELETE' },
    );
  }

  getCreateCounts(): Promise<{ allEntities: number }> {
    return this.makeRequest<{ allEntities: number }>(`${this.config.baseEndpoint}/create-counts`);
  }

  // Update a single field of an entity
  async updateEntityField({ entityId, field, value }: UpdateEntityFieldParams): Promise<Entity> {
    const updateData = buildUpdatePayload(field, value);
    const entity = await this.makeRequest<Entity>(`${this.config.baseEndpoint}/${entityId}`, {
      method: 'PATCH',
      body: updateData
    });
    return flattenEntityCustomFields(entity);
  }

  // Import/Export operations
  previewImport(records: unknown[], options: ImportOptions): Promise<ImportPreviewRecord[]> {
    return this.makeRequest<ImportPreviewRecord[]>(`${this.config.baseEndpoint}/import/preview`, {
      method: 'POST',
      body: { records, options }
    });
  }

  import(data: { records: ImportPreviewRecord[]; options: ImportOptions }): Promise<ImportResult> {
    return this.makeRequest<ImportResult>(`${this.config.baseEndpoint}/import`, {
      method: 'POST',
      body: data
    });
  }

  export(options: ExportOptions): Promise<Entity[]> {
    return this.makeRequest<Entity[]>(`${this.config.baseEndpoint}/export`, {
      method: 'POST',
      body: options
    });
  }

  // Logo management
  uploadLogo(entityId: string, file: File): Promise<{ logoUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.makeRequest<{ logoUrl: string }>(`${this.config.baseEndpoint}/${entityId}/logo`, {
      method: 'POST',
      body: formData
    });
  }

  getLogoUrl(entityId: string): Promise<{ logoUrl: string | null }> {
    return this.makeRequest<{ logoUrl: string | null }>(`${this.config.baseEndpoint}/${entityId}/logo`);
  }

  bulkGetLogoUrls(entityIds: string[]): Promise<Record<string, string | null>> {
    return this.makeRequest<{ logoUrls: Record<string, string | null> }>(`${this.config.baseEndpoint}/logos/bulk`, {
      method: 'POST',
      body: { entityIds }
    }).then(res => res.logoUrls);
  }

  removeLogo(entityId: string): Promise<Entity> {
    return this.makeRequest<Entity>(`${this.config.baseEndpoint}/${entityId}/logo`, {
      method: 'DELETE'
    });
  }

  // Bulk delete entities
  bulkDeleteEntities(ids: string[]): Promise<{ deletedCount: number }> {
    return this.makeRequest<{ deletedCount: number }>(`${this.config.baseEndpoint}/bulk-delete`, {
      method: 'POST',
      body: { ids }
    });
  }
}

// Export singleton instance
export const entityApi = createSingletonApi(EntityApiService);
